import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import Row from '../../../components/Row'
import BoxContainer from '../../../fragments/BoxContainer'
import Column from '../../../components/Column'
import Text from '../../../components/Text'
import { formatCurrency, formatPrice } from '../../../utils'
import Button from '../../../components/Button'
import { useLocation, useNavigate } from 'react-router-dom'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers'
import { AppRoutes } from '../../../interfaces/store/appStore'
import PaymentMethods from '../components/PaymentMethods'
import { useBillingHandlers } from '../../../services/actions/billing/useBillingHandlers'
import ActionText from '../../../components/ActionText'
import { useAuthStore } from '../../../stores/auth/useAuthStore'
import { IStripePaymentMethod } from '../../../interfaces/models/user'
import { subscriptionNeedsVerification } from '../../../services/apis/billing/subscriptionNeedsVerification'
import { getPlanDisplayName } from '../v2/pricingV2'

interface IPaymentSectionProps { }

const PaymentSection: FunctionComponent<IPaymentSectionProps> = () => {

    const location = useLocation()
    const navigate = useNavigate()
    const { store: { subscription } } = useAuthStore()
    let { setupIntent, selectedPrice, selectedBillingPlan, quantity, payNow, totals, appliedCoupon, prorationDate } = location.state || {}

    const [ paymentMethodId, setPaymentMethodId ] = useState<string>("")
    const [ showPaymentElement, setShowPaymentElement] = useState<boolean>(false)
    const [ paymentElementMounted, setPaymentElementMounted ] = useState<boolean>(false)
    const [buttonState, setButtonState] = useState<string>('')
    const [cardComplete, setCardComplete] = useState(false);

    const { buySubscriptionHandler, setDefaultPaymentMethodHandler } = useBillingHandlers()

    const stripe = useStripe()
    const elements = useElements()

    const { showErrorToast } = useAppHandlers()

    const validateForm = useCallback(() => {
        return !(!quantity || !setupIntent || !selectedPrice || !selectedBillingPlan)
    },
    [stripe, elements, quantity, setupIntent, selectedPrice, selectedBillingPlan])

    const fallbackSeats = 1

    useEffect(() => {
        const hasPaymentMethods = !!subscription?.paymentMethods?.length

        // Auto-open only when there are no saved payment methods.
        // Otherwise let the user toggle it via "Add another one".
        if (!hasPaymentMethods && !showPaymentElement) {
            setPaymentElementMounted(true)
            setShowPaymentElement(true)
        }
    }, [subscription?.paymentMethods?.length, showPaymentElement])
    
    
    const setShowPaymenElementHandler = useCallback(() => {   
        const newShowPaymentElement = !showPaymentElement
        setShowPaymentElement(!showPaymentElement);
        if(newShowPaymentElement) {
            setPaymentMethodId("")
            setPaymentElementMounted(true)
        }
    }, [showPaymentElement])

    const confirmStripePaymentHandler = async (event: any) => {
        event.preventDefault();
        setButtonState('isLoading')
        const isValid = await validateForm()
        if (!isValid || !stripe || !elements) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }
        const params = [
            ["payNow", payNow],
            ["customerId", setupIntent?.customer],
            ["quantity", selectedBillingPlan?.name === 'Starter' ? 1 : quantity],
            ["priceId", selectedPrice.priceId],
            ["currency", selectedPrice.currency]
        ]
        if (appliedCoupon) params.push(["couponId", appliedCoupon.id])
        if (prorationDate) params.push(["prorationDate", prorationDate])

        const redirectParams = params.reduce((string, param) => {
            const append = param[1] ? `${!string.length ? "?" : "&"}${param[0]}=${param[1]}` : ""
            return `${string}${append}`
        }, "")

        // Saved card: buy straight away. If the charge still needs SCA / 3D Secure
        // verification, the PaymentVerificationBanner on the billing screen drives
        // it - we never open a second Stripe modal here.
        if (paymentMethodId.length) {
            const subscriptionResult = await buySubscriptionHandler({ paymentMethodId, customerId: setupIntent?.customer, licenceQuantity: selectedBillingPlan?.name === 'Starter' ? 1 : quantity, priceId: selectedPrice.priceId, ...appliedCoupon && { couponId: appliedCoupon.id }, ...prorationDate && { prorationDate }, currency: selectedPrice.currency })

            if (!subscriptionResult) {
                setButtonState('')
                return false
            }

            if (subscriptionNeedsVerification(subscriptionResult)) {
                navigate(AppRoutes.SETTINGS_BILLING)
                setButtonState('')
                return true
            }

            navigate(`${AppRoutes.SETTINGS_BILLING}${payNow ? '?successModal=true' : ''}`)
            setButtonState('')
            return true
        }

        // New card: hand off to Stripe, which redirects to the processing page.
        const result = await stripe.confirmSetup({
            //`Elements` instance that was used to create the Payment Element
            elements,
            confirmParams: {
                return_url: `${process.env.REACT_APP_APP_URL}${AppRoutes.BILLING_PROCESSING}${redirectParams.length ? redirectParams : ""}`,
            },
        });

        if (!result || result.error) {
            result?.error && showErrorToast({message: result.error.message ?? 'An error occured.'})
            setButtonState('')
            return false
        }
        // Your customer will be redirected to your `return_url`. For some payment
        // methods like iDEAL, your customer will be redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.
        setButtonState('')
        return true
    }

    return <Container>
         <StyledColumn>
            <NarrowColumn>
                <Text $heading2 $bold $black>{'Check out'}</Text>
                <StyledBoxedContainer>
                    <Text $bold $heading3>{'Payment method details'}</Text>
                    {payNow && subscription?.paymentMethods?.length > 0 && <Text $heading5>{'Choose a payment method below'}</Text> }
                    {(!payNow || subscription?.paymentMethods?.length > 0) && <PaymentMethods
                        $height='135px'
                        notifyOnInit={!!payNow}
                        onChangeHandler={payNow
                            ? (paymentId: string) => setPaymentMethodId(paymentId)
                            : (paymentId: string) => {
                                const currentDefaultId = subscription?.paymentMethods?.find((pm: IStripePaymentMethod) => pm?.isDefault)?.id
                                if (currentDefaultId && paymentId === currentDefaultId) return
                                return setDefaultPaymentMethodHandler(paymentId)
                            }
                        }
                    />}
                    {(!payNow || subscription?.paymentMethods?.length > 0) && <ActionText onClickHandler={() => setShowPaymenElementHandler()} $heading5>{'Add another one'}</ActionText>}
                    {paymentElementMounted && <div style={{ display: showPaymentElement ? 'block' : 'none', width: '100%' }}>
                        <PaymentElement
                            className='ld-stripe-element'
                            onChange={(e) => setCardComplete(e.complete)} />
                    {!payNow && <Button 
                        $type={'blue'} 
                        disabled={!cardComplete}
                        onClickHandler={confirmStripePaymentHandler}>
                            {'Add payment method'}
                    </Button>}
                    </div>}
                    
                </StyledBoxedContainer>
            </NarrowColumn>
        </StyledColumn>
        <StyledOutsideBoxedContainer>
        <NarrowColumn>
                <StyledRow alignItems spaceBetween>
                    <Text $heading2 $bold $black $capitalize>{'Subscription summary'}</Text>
                </StyledRow>
                <StyledInvisibleBox>
                    <Text $heading3>{getPlanDisplayName(selectedBillingPlan?.name) ?? ''}</Text>
                    <StyledRow alignItems spaceBetween>
                        <Row gap="3px" alignItems>
                            <Text $paragraph1 $capitalize>{selectedPrice?.interval ? `${selectedPrice?.interval}ly subscription` : ''}</Text>
                            {fallbackSeats > 1 && <Text $faded>(includes ${fallbackSeats} seats)</Text>}
                        </Row>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(selectedPrice?.price ?? 0)}`}</Text>
                    </StyledRow>
                    {selectedPrice?.additionalLicence && quantity && quantity - fallbackSeats > 0 && <StyledRow alignItems spaceBetween>
                        <Text $paragraph1>{`Additional seat(s) x ${quantity - fallbackSeats}`}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(selectedPrice?.additionalLicence ? (quantity - fallbackSeats) * selectedPrice?.additionalLicence: 0)}`}</Text>
                    </StyledRow>}
                    <Divider />
                    {!payNow && <StyledRow alignItems spaceBetween>
                        <Text $heading5 $capitalize>{`Total Billed ${selectedPrice?.interval}ly`}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice(selectedPrice?.additionalLicence && selectedPrice?.price ? (quantity - fallbackSeats) * selectedPrice?.additionalLicence + selectedPrice?.price : selectedPrice?.price ?? 0)}`}</Text>
                    </StyledRow>}
                    {totals && <><StyledRow alignItems spaceBetween>
                        <Text $paragraph1 $capitalize>{'Discount'}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice((totals.discountAmount ?? 0) * 100)}`}</Text>
                    </StyledRow>
                    <StyledRow alignItems spaceBetween>
                        <Text $paragraph1 $capitalize>{'Subtotal'}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice((totals.subtotal ?? 0) * 100)}`}</Text>
                    </StyledRow>
                    <Divider />
                    <StyledRow alignItems spaceBetween>
                        <Text $heading5 $capitalize>{`Total Billed ${selectedPrice?.interval}ly`}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice((totals.total ?? 0) * 100)}`}</Text>
                    </StyledRow></>}
                    <Text $lighter $paragraph3>All payments are powered by Stripe.com. Your personal information will never be shared, and all transactions are protected by Stripe.com.</Text>
                    <svg xmlns="http://www.w3.org/2000/svg" width="150" height="34" viewBox="0 0 150 34">
                        <g fill="none" fillRule="evenodd">
                            <g fill="#8C96AD" fillRule="nonzero">
                                <g>
                                    <path d="M146 0H3.73C1.67 0 0 1.67 0 3.73v26.54C0 32.33 1.67 34 3.73 34H146c2.21 0 4-1.79 4-4V4c0-2.21-1.79-4-4-4zm3 30c0 1.657-1.343 3-3 3H3.73c-1.505-.005-2.725-1.225-2.73-2.73V3.73C1.005 2.225 2.225 1.005 3.73 1H146c1.657 0 3 1.343 3 3v26z" transform="translate(-520 -940) translate(520 940)" />
                                    <path d="M17.07 11.24h-4.3V22h1.92v-4.16h2.38c2.4 0 3.9-1.16 3.9-3.3 0-2.14-1.5-3.3-3.9-3.3zm-.1 5h-2.28v-3.3H17c1.38 0 2.11.59 2.11 1.65s-.76 1.6-2.11 1.6l-.03.05zM25.1 14c-1.063-.012-2.082.425-2.806 1.204-.723.779-1.084 1.827-.994 2.886-.128 1.436.566 2.822 1.793 3.58 1.227.757 2.777.757 4.004 0 1.227-.758 1.92-2.144 1.793-3.58.093-1.058-.265-2.107-.988-2.886-.722-.78-1.74-1.217-2.802-1.204zm0 6.67c-1.22 0-2-1-2-2.58s.76-2.58 2-2.58 2 1 2 2.58-.79 2.57-2 2.57v.01zM36.78 19.35L35.37 14.13 33.89 14.13 32.49 19.35 31.07 14.13 29.22 14.13 31.59 22.01 33.15 22.01 34.59 16.85 36.03 22.01 37.59 22.01 39.96 14.13 38.18 14.13zM44 14c-1.049.02-2.043.468-2.752 1.241-.709.773-1.07 1.803-.998 2.849-.085 1.063.283 2.113 1.012 2.89.729.78 1.752 1.215 2.818 1.2 1.487.08 2.859-.797 3.41-2.18L46 19.38c-.24.796-1 1.319-1.83 1.26-.58.014-1.14-.21-1.55-.62-.41-.41-.634-.97-.62-1.55h5.52v-.6C47.54 15.71 46.32 14 44 14zm-1.93 3.13c.145-.95.97-1.646 1.93-1.63.446-.038.887.118 1.21.428.324.31.499.744.48 1.192l-3.62.01zM50.69 15.3v-1.17h-1.8V22h1.8v-4.13c-.032-.54.168-1.068.55-1.45.382-.382.91-.582 1.45-.55.22-.016.44-.016.66 0v-1.8h-.51c-.896-.046-1.736.434-2.15 1.23zM57.48 14c-1.049.02-2.043.468-2.752 1.241-.709.773-1.07 1.803-.998 2.849-.085 1.063.283 2.113 1.012 2.89.729.78 1.752 1.215 2.818 1.2 1.472.063 2.824-.811 3.37-2.18l-1.54-.59c-.24.796-1 1.319-1.83 1.26-.568-.005-1.11-.238-1.504-.646-.395-.407-.61-.957-.596-1.524H61v-.6c0-2.19-1.24-3.9-3.52-3.9zm-1.93 3.13c.149-.942.967-1.632 1.92-1.62.446-.038.887.118 1.21.428.324.31.499.744.48 1.192h-3.61zM67.56 15c-.562-.66-1.394-1.028-2.26-1-2.21 0-3.47 1.85-3.47 4.09 0 2.24 1.26 4.09 3.47 4.09.867.032 1.7-.337 2.26-1V22h1.8V11.24h-1.8V15zm0 3.35c.082.577-.092 1.16-.477 1.599-.384.438-.94.687-1.523.681-1.31 0-2-1-2-2.52s.7-2.52 2-2.52c1.11 0 2 .81 2 2.29v.47zM79.31 14c-.883-.039-1.734.33-2.31 1v-3.76h-1.8V22H77v-.83c.565.661 1.4 1.03 2.27 1 2.2 0 3.46-1.86 3.46-4.09 0-2.23-1.22-4.08-3.42-4.08zM79 20.6c-.583.006-1.139-.243-1.523-.681-.385-.438-.559-1.022-.477-1.599v-.47c0-1.48.84-2.29 2-2.29 1.3 0 2 1 2 2.52s-.75 2.52-2 2.52zM86.93 19.66L85 14.13h-1.9l2.9 7.59-.3.74c-.106.527-.61.876-1.14.79-.2.015-.4.015-.6 0v1.51c.242.036.486.053.73.05 1.288.094 2.46-.749 2.78-2l3.24-8.62h-1.89l-1.89 5.47zM125 12.43c-.797-.005-1.564.308-2.13.87l-.14-.69h-2.39v12.92l2.72-.59v-3.13c.541.453 1.224.7 1.93.7 1.94 0 3.72-1.59 3.72-5.11 0-3.22-1.8-4.97-3.71-4.97zm-.65 7.63c-.483.028-.953-.163-1.28-.52v-4.11c.328-.37.806-.572 1.3-.55 1 0 1.68 1.13 1.68 2.58 0 1.45-.69 2.6-1.7 2.6zM133.73 12.43c-2.62 0-4.21 2.26-4.21 5.11 0 3.37 1.88 5.08 4.56 5.08 1.046.017 2.079-.234 3-.73v-2.25c-.837.422-1.763.635-2.7.62-1.08 0-2-.39-2.14-1.7h5.38v-1c.09-2.87-1.27-5.13-3.89-5.13zm-1.47 4.07c0-1.26.77-1.79 1.45-1.79.68 0 1.4.53 1.4 1.79h-2.85zM113 13.36l-.17-.82h-2.32v9.71h2.68v-6.58c.496-.602 1.313-.833 2.05-.58v-2.55c-.847-.332-1.808.02-2.24.82zM99.46 15.46c0-.44.36-.61.93-.61.945.02 1.87.267 2.7.72v-2.63c-.857-.347-1.775-.52-2.7-.51-2.21 0-3.68 1.18-3.68 3.16 0 3.1 4.14 2.6 4.14 3.93 0 .52-.44.69-1 .69-1.057-.062-2.084-.37-3-.9V22c.944.421 1.966.64 3 .64 2.26 0 3.82-1.15 3.82-3.16-.05-3.36-4.21-2.76-4.21-4.02zM107.28 10.24l-2.65.58v8.93c-.028.76.259 1.499.792 2.042.534.543 1.267.842 2.028.828.657.03 1.312-.097 1.91-.37V20c-.35.15-2.06.66-2.06-1v-4h2.06v-2.34h-2.06l-.02-2.42zM116.25 11.7L118.98 11.13 118.98 8.97 116.25 9.54zM116.25 12.61H118.98V22.25H116.25z" transform="translate(-520 -940) translate(520 940)" />
                                </g>
                            </g>
                        </g>
                    </svg>
                    <Divider />
                    {totals && <StyledRow alignItems spaceBetween>
                        <Text $heading4 $capitalize>{`Total to pay`}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice((totals.totalToPay ?? 0) * 100)}`}</Text>
                    </StyledRow> }
                    {totals && totals.stripeCredits !== 0 && <StyledRow alignItems spaceBetween>
                        <Text $heading4 $capitalize>{`Stripe credits`}</Text>
                        <Text $paragraph1>{`${formatCurrency(selectedPrice?.currency ?? 'usd')}${formatPrice((totals.stripeCredits ?? 0) * 100)}`}</Text>
                    </StyledRow> }
                    {payNow && <Button isLoading={buttonState === 'isLoading'} $type={'blue'} onClickHandler={confirmStripePaymentHandler}>Pay now</Button>}{/* disabled={!paymentFormValid} */}
                </StyledInvisibleBox>
            </NarrowColumn>
        </StyledOutsideBoxedContainer>
    </Container>
}

const Container = styled.div`
    width: 100%;
    display: flex;
    height: 100%;
    flex-wrap: wrap;
    .ld-stripe-element {
        width: 100%;
    }
`

const StyledColumn = styled(Column)`
    flex: 1;
    padding: 16px;
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    gap: 32px;
    align-items: center;
    @media only screen and (min-width: 1264px) {
        padding: 60px;
        align-items: flex-end;
    }
`

const NarrowColumn = styled(Column)`
    width: 500px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    @media only screen and (max-width: 598px) {
        width: auto;
        max-width: 500px;
    }
`

const StyledOutsideBoxedContainer = styled(BoxContainer)`
    align-items: flex-start;
    gap: 32px;
    flex: 1;
    padding: 16px;
    align-items: center;
    @media only screen and (min-width: 1264px) {
        padding: 60px;
        align-items: flex-start;
    }
`

const StyledInvisibleBox = styled(Column)`
    width: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    gap: 15px;
    padding: 16px 0px;
`
const StyledBoxedContainer = styled(BoxContainer)`
    align-items: flex-start;
`
const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: ${({ theme: { quinaryColor } }) => `${quinaryColor}`};
    margin: 10px 0;
`

const StyledRow = styled(Row)`
    width: 100%;
`

export default PaymentSection
